import React from "react";
import { Spin, Tag } from "antd";
import type { Recipe } from "../../../Types";

interface Props {
  recipe: Recipe | null;
  recipeLoading: boolean;
  productionQty: number;
  additionalCost: number;
}

function scaleIngredients(recipe: Recipe, qty: number) {
  const scale = qty / (recipe.batch_size || 1);
  return recipe?.items?.map((item) => {
    const totalQty = (item.quantity + item.wastage_quantity) * scale;
    const cost = item.cost ?? 0;
    const unitCost = cost / (item.quantity + item.wastage_quantity || 1);
    return { ...item, scaledQty: totalQty, lineCost: totalQty * unitCost };
  });
}

export const RecipePreview: React.FC<Props> = ({
  recipe,
  recipeLoading,
  productionQty,
  additionalCost,
}) => {
  if (recipeLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-gray-400 gap-2">
        <Spin size="default" className="text-teal-600" />
        <span className="text-xs font-medium">Loading recipe details…</span>
      </div>
    );
  }

  if (!recipe || !recipe.items || recipe.items.length === 0) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700 font-medium">
        No recipe found for this product. Please define a recipe before
        recording production.
      </div>
    );
  }

  const scaled = scaleIngredients(recipe, productionQty);
  const ingredientTotal = scaled?.reduce((s, i) => s + (i.lineCost || 0), 0) || 0;
  const grandTotal = ingredientTotal + (additionalCost || 0);
  const displayTotal = isNaN(grandTotal) ? 0 : grandTotal;
  const scaleFactor =
    productionQty > 0 ? productionQty / (recipe.batch_size || 1) : 0;

  return (
    <div className="space-y-3">
      {/* Scale metadata details */}
      <div className="grid grid-cols-2 gap-2">
        <div className="flex items-center justify-between rounded bg-slate-50 border border-slate-100 px-3 py-2 text-xs">
          <span className="text-gray-500 font-medium">Base Batch Size</span>
          <Tag
            bordered={false}
            color="blue"
            className="m-0 font-bold font-mono"
          >
            {recipe.batch_size ?? 1} units
          </Tag>
        </div>
        <div className="flex items-center justify-between rounded bg-slate-50 border border-slate-100 px-3 py-2 text-xs">
          <span className="text-gray-500 font-medium">Scale Factor</span>
          <Tag
            bordered={false}
            color="purple"
            className="m-0 font-bold font-mono"
          >
            ×{scaleFactor.toFixed(2)}
          </Tag>
        </div>
      </div>

      {/* Ingredients list */}
      {scaled?.length > 0 && (
        <div className="overflow-hidden rounded border border-gray-200">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 text-gray-500 border-b border-gray-200">
                <th className="px-3 py-2 text-left font-semibold">
                  Ingredient
                </th>
                <th className="px-3 py-2 text-right font-semibold">Unit</th>
                <th className="px-3 py-2 text-right font-semibold">
                  Qty Needed
                </th>
              </tr>
            </thead>
            <tbody>
              {scaled.map((item, idx) => (
                <tr
                  key={idx}
                  className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-3 py-2">
                    <div className="font-bold text-gray-700">
                      {item.raw_material_name}
                    </div>
                    {item.wastage_quantity > 0 && (
                      <div className="text-[10px] text-amber-600 font-medium">
                        +
                        {(
                          (item.wastage_quantity /
                            (item.quantity + item.wastage_quantity)) *
                          100
                        ).toFixed(0)}
                        % wastage included
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-400 font-medium">
                    {item.raw_material_unit}
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-rose-600">
                    {item.scaledQty.toFixed(3)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Cost summary bar */}
      <div className="rounded border border-teal-100 bg-teal-50 px-4 py-2.5 flex items-center justify-between text-xs">
        <span className="font-semibold text-teal-800">
          Estimated Production Cost
        </span>
        <span className="font-mono font-extrabold text-teal-700 text-sm">
          LKR {displayTotal.toLocaleString("en-LK", { minimumFractionDigits: 2 })}
        </span>
      </div>
    </div>
  );
};
